import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { LaunchCreative } from './types';

/**
 * One asset's thumbnail. Dropbox renders these for video as well as images, so
 * a story mp4 costs ~5KB here instead of the ~43MB the file itself would.
 *
 * A failed load shows the label rather than a broken-image glyph: a thumbnail
 * Dropbox cannot generate (an unusual codec) is not the same as a missing file,
 * and the row is still launchable either way.
 */
function Thumb({ src, full, label, aspect }: { src: string | null; full: string | null; label: string; aspect: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <Box sx={{
        width: 96, height: 96, borderRadius: 1, bgcolor: 'action.hover',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Typography variant="caption" color="text.secondary">{src ? 'no preview' : label}</Typography>
      </Box>
    );
  }
  return (
    <Stack alignItems="center" spacing={0.5}>
      <Box
        component="a"
        href={full ?? src}
        target="_blank"
        rel="noopener"
        sx={{ lineHeight: 0 }}
      >
        <Box
          component="img"
          src={src}
          alt={label}
          onError={() => setFailed(true)}
          sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">{aspect}</Typography>
    </Stack>
  );
}

/**
 * The five headline/caption options.
 *
 * Headlines are the scannable part and captions are three paragraphs each, so
 * rendering all five in full stacked a screen-and-a-half of text into a table
 * row — and because captions vary wildly in length, a two-column grid of them
 * went ragged. Headlines list on the left, the selected caption on the right in
 * a pane of fixed height, so the row is the same size whichever option is open.
 */
function CopyPreview({ creative }: { creative: LaunchCreative }) {
  const count = Math.max(creative.headlines.length, creative.captions.length);
  const [active, setActive] = useState(0);
  if (!count) return null;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Copy — {count} option{count === 1 ? '' : 's'}, all rotated by Meta
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, gap: 2, mt: 0.5 }}>
        <Box>
          {Array.from({ length: count }).map((_, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              onClick={() => setActive(i)}
              sx={{
                display: 'flex', gap: 1, alignItems: 'baseline', cursor: 'pointer',
                px: 1, py: 0.75, borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: i === active ? 'primary.main' : 'transparent',
                bgcolor: i === active ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: i === active ? 'action.selected' : 'action.hover' },
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 14 }}>{i + 1}</Typography>
              <Typography variant="body2" sx={{ fontWeight: i === active ? 600 : 400 }}>
                {creative.headlines[i] || <em>no headline</em>}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{
          p: 1.5, borderRadius: 1, bgcolor: 'action.hover',
          maxHeight: 260, overflowY: 'auto',
        }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Caption {active + 1}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {creative.captions[active] || '—'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

type SortKey = 'name' | 'product' | 'adType' | 'copy' | 'createdTime' | 'status';

const SORTERS: Record<SortKey, (c: LaunchCreative) => string | number> = {
  name: c => c.name.toLowerCase(),
  product: c => c.product.toLowerCase(),
  adType: c => (c.adType || '').toLowerCase(),
  copy: c => Math.min(c.headlines.length, c.captions.length),
  createdTime: c => c.createdTime,
  status: c => (c.launched ? 1 : 0),
};

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Creative' },
  { key: 'product', label: 'Product' },
  { key: 'adType', label: 'Type' },
  { key: 'copy', label: 'Copy' },
  { key: 'status', label: 'Assets' },
  { key: 'createdTime', label: 'Added' },
];

export default function CreativeTable({
  creatives,
  pinnedRows = [],
  onUnpin,
  selectedIds,
  onToggle,
  onTogglePage,
  disabled,
}: {
  creatives: LaunchCreative[];
  /** Frozen at the top of every page, outside the sort and the pagination. */
  pinnedRows?: LaunchCreative[];
  onUnpin?: () => void;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onTogglePage: (ids: string[], select: boolean) => void;
  disabled: boolean;
}) {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('createdTime');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const get = SORTERS[sortKey];
    return [...creatives].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av === bv) return a.name.localeCompare(b.name);
      const cmp = av < bv ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [creatives, sortKey, sortDir]);

  const sortBy = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'createdTime' ? 'desc' : 'asc'); }
    setPage(0);
  };

  // Clamp rather than let a filter change strand the view on an empty page.
  const lastPage = Math.max(0, Math.ceil(sorted.length / perPage) - 1);
  const current = Math.min(page, lastPage);
  const rows = sorted.slice(current * perPage, current * perPage + perPage);
  // A launched row cannot be launched again, so it is never part of a
  // select-all — otherwise "select page" would arm rows the API refuses.
  const pageIds = rows.filter(r => !r.launched).map(r => r.notionPageId);
  const pageSelected = pageIds.filter(id => selectedIds.has(id)).length;

  const toggleOpen = (id: string) =>
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // One row. Shared so a frozen row and a flow row cannot drift apart.
  const renderRow = (c: LaunchCreative, isPinned = false) => {
    const expanded = open.has(c.notionPageId);
    const copyCount = Math.min(c.headlines.length, c.captions.length);
    return (
      <React.Fragment key={c.notionPageId}>
        <TableRow
          hover
          selected={selectedIds.has(c.notionPageId)}
          sx={{
            ...(c.launched ? { opacity: 0.55 } : {}),
            ...(isPinned ? { bgcolor: 'action.hover', '& > td': { borderBottomColor: 'primary.main' } } : {}),
          }}
        >
          <TableCell padding="checkbox">
            <Tooltip title={c.launched ? 'Already launched — tagged Launched in Notion' : ''}>
              <span>
                <Checkbox
                  size="small"
                  checked={selectedIds.has(c.notionPageId)}
                  onChange={() => onToggle(c.notionPageId)}
                  disabled={disabled || Boolean(c.launched)}
                />
              </span>
            </Tooltip>
          </TableCell>
          <TableCell>
            <IconButton size="small" onClick={() => toggleOpen(c.notionPageId)} disabled={Boolean(c.launched)}>
              {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2">{c.name}</Typography>
              {c.launched && <Chip size="small" label="launched" />}
              {!c.launched && c.readyMarker && (
                <Tooltip title='Marked "this one is ready" in Notion'>
                  <Chip size="small" color="success" variant="outlined" label="ready" />
                </Tooltip>
              )}
              <Link href={c.notionPageUrl} target="_blank" rel="noopener" sx={{ display: 'flex' }}>
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            </Stack>
          </TableCell>
          <TableCell><Typography variant="caption">{c.product}</Typography></TableCell>
          <TableCell><Chip size="small" variant="outlined" label={c.adType} /></TableCell>
          <TableCell><Typography variant="caption">{c.launched ? '—' : copyCount}</Typography></TableCell>
          <TableCell>
            {c.launched ? (
              <Typography variant="caption" color="text.secondary">not loaded</Typography>
            ) : (
              <Stack direction="row" spacing={0.5}>
                {/* "on page" says the asset is the image attached to the Notion
                    row rather than a Dropbox link — it launches either way, but
                    the provenance is worth being able to see. */}
                <Tooltip title={c.feedFromPage ? 'Using the image attached to the Notion page — there is no Dropbox link for it' : ''}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={c.feedUrl ? 'default' : 'error'}
                    label={c.feedUrl ? (c.feedFromPage ? 'feed · on page' : 'feed') : 'no feed'}
                  />
                </Tooltip>
                <Tooltip title={c.storyFromPage ? 'Using the image attached to the Notion page — there is no Dropbox link for it' : ''}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={c.storyUrl ? 'default' : 'error'}
                    label={c.storyUrl ? (c.storyFromPage ? 'story · on page' : 'story') : 'no story'}
                  />
                </Tooltip>
              </Stack>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="caption" color="text.secondary">
              {new Date(c.createdTime).toLocaleDateString()}
            </Typography>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
            <Collapse in={expanded} unmountOnExit>
              <Box sx={{ py: 2 }}>
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Assets
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                      <Thumb src={c.feedThumb} full={c.feedFull} label="feed" aspect="feed" />
                      <Thumb src={c.storyThumb} full={c.storyFull} label="story" aspect="story 9:16" />
                    </Stack>
                    {c.extraAssets.length > 0 && (
                      <Typography variant="caption" color="warning.main" component="div" sx={{ mt: 0.5, maxWidth: 210 }}>
                        {c.extraAssets.length} extra Dropbox link(s) ignored — the first of each aspect is used.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CopyPreview creative={c} />
                  </Box>
                </Stack>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={pageIds.length > 0 && pageSelected === pageIds.length}
                indeterminate={pageSelected > 0 && pageSelected < pageIds.length}
                onChange={e => onTogglePage(pageIds, e.target.checked)}
                disabled={disabled}
              />
            </TableCell>
            <TableCell sx={{ width: 40 }} />
            {COLUMNS.map(col => (
              <TableCell key={col.key} sortDirection={sortKey === col.key ? sortDir : false}>
                <TableSortLabel
                  active={sortKey === col.key}
                  direction={sortKey === col.key ? sortDir : 'asc'}
                  onClick={() => sortBy(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {pinnedRows.length > 0 && (
            <TableRow>
              <TableCell colSpan={8} sx={{ py: 0.5, bgcolor: 'action.hover' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Pinned — {pinnedRows.length} from the batch you picked
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    held at the top whatever the sort, filter or page says
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {onUnpin && (
                    <Link component="button" variant="caption" onClick={onUnpin} sx={{ cursor: 'pointer' }}>
                      unpin
                    </Link>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          )}
          {pinnedRows.map(c => renderRow(c, true))}
          {rows.map(c => renderRow(c))}
          {rows.length === 0 && pinnedRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Nothing matches these filters.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={sorted.length}
        page={current}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={perPage}
        onRowsPerPageChange={e => { setPerPage(Number(e.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </>
  );
}
